import {
  isSmartCreationDraftCampaignSelected,
  type SmartCreationDraftContentV2,
  SmartCreationDraftContentV2Schema,
} from "../schemas/smart-creation";
import type { SmartCreationReviewGraph } from "../schemas/smart-creation-review";
import { SmartCreationReviewError } from "./errors";

export function updateDraftContentFromGraph(
  draftContent: SmartCreationDraftContentV2,
  graph: SmartCreationReviewGraph,
): SmartCreationDraftContentV2 {
  const campaigns = draftContent.campaigns.map((campaign) => {
    const reviewCampaign = graph.campaigns.find(
      (candidate) => candidate.ref.itemKey === campaign.itemKey,
    );
    if (!reviewCampaign) throw new Error("Review Campaign projection is incomplete");
    const adGroups = campaign.adGroups.map((adGroup) => {
      const reviewAdGroup = graph.adGroups.find(
        (candidate) => candidate.ref.itemKey === adGroup.itemKey,
      );
      if (!reviewAdGroup) throw new Error("Review Ad Group projection is incomplete");
      const targets = reviewAdGroup.targetLineageKeys.map((lineageKey) => {
        const reviewed = graph.targets.find((candidate) => candidate.ref.lineageKey === lineageKey);
        if (!reviewed?.ref.itemKey) {
          throw new Error("Review Target projection is incomplete");
        }
        const common = {
          itemKey: reviewed.ref.itemKey,
          sourceTargetId: reviewed.sourceTargetId,
          strategyKind: reviewed.strategyKind,
          bid: reviewed.bid.effective,
        };
        if (adGroup.targeting.type === "automatic" && reviewed.type === "automatic") {
          return {
            ...common,
            strategyKind: "automatic" as const,
            expressionType: reviewed.expressionType,
            ...(reviewed.excluded.effective ? { included: false } : {}),
          };
        }
        if (adGroup.targeting.type === "keyword" && reviewed.type === "keyword") {
          if (reviewed.strategyKind === "automatic") {
            throw new Error("Keyword Target cannot use the automatic Strategy kind");
          }
          return { ...common, strategyKind: reviewed.strategyKind, keyword: reviewed.keyword };
        }
        if (adGroup.targeting.type === "product" && reviewed.type === "product") {
          if (reviewed.strategyKind === "automatic") {
            throw new Error("Product Target cannot use the automatic Strategy kind");
          }
          return {
            ...common,
            strategyKind: reviewed.strategyKind,
            asin: reviewed.asin,
            ...(reviewed.matchType === undefined ? {} : { matchType: reviewed.matchType }),
          };
        }
        if (adGroup.targeting.type === "category" && reviewed.type === "category") {
          if (reviewed.strategyKind === "automatic") {
            throw new Error("Category Target cannot use the automatic Strategy kind");
          }
          return {
            ...common,
            strategyKind: reviewed.strategyKind,
            categoryId: reviewed.categoryId,
          };
        }
        if (adGroup.targeting.type === "category_brand" && reviewed.type === "category_brand") {
          if (reviewed.strategyKind !== "competitor" && reviewed.strategyKind !== "branded") {
            throw new Error("Category-brand Target requires a brand Strategy kind");
          }
          return {
            ...common,
            strategyKind: reviewed.strategyKind,
            categoryId: reviewed.categoryId,
            brandId: reviewed.brandId,
          };
        }
        throw new SmartCreationReviewError(
          422,
          "smart_creation.review_target_invalid",
          "Target type does not match its Ad Group targeting lane",
        );
      });
      return {
        ...adGroup,
        name: reviewAdGroup.name.effective,
        defaultBid: reviewAdGroup.defaultBid.effective,
        targeting: { ...adGroup.targeting, targets } as typeof adGroup.targeting,
      };
    });
    return {
      ...campaign,
      name: reviewCampaign.name.effective,
      dailyBudget: reviewCampaign.dailyBudget.effective,
      biddingStrategy: reviewCampaign.biddingStrategy.effective,
      adGroups,
    };
  });
  return SmartCreationDraftContentV2Schema.parse({
    ...draftContent,
    campaigns,
    summary: {
      ...draftContent.summary,
      campaignCount: campaigns.length,
      adGroupCount: campaigns.reduce((count, campaign) => count + campaign.adGroups.length, 0),
      productAdCount: campaigns.reduce(
        (count, campaign) =>
          count +
          campaign.adGroups.reduce(
            (campaignCount, adGroup) => campaignCount + adGroup.products.length,
            0,
          ),
        0,
      ),
      targetCount: campaigns.reduce(
        (count, campaign) =>
          count +
          campaign.adGroups.reduce(
            (campaignCount, adGroup) => campaignCount + adGroup.targeting.targets.length,
            0,
          ),
        0,
      ),
      totalDailyBudget: campaigns.reduce(
        (total, campaign) =>
          total + (isSmartCreationDraftCampaignSelected(campaign) ? campaign.dailyBudget : 0),
        0,
      ),
    },
  });
}
